import React from 'react';
import { Calculator, Activity } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров расчета
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating
}) => {
  // Парсинг числовых значений
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };

  // Обработчик изменения веса
  const handleWeightChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      weight: newValue
    }));
  };
  
  // Обработчик изменения уровня активности
  const handleActivityChange = (newValue) => {
    setFormData(prev => ({
      ...prev,
      activityLevel: newValue
    }));
  };

  // Обработчик изменения цели
  const handleGoalChange = (newValue) => {
    setFormData(prev => ({
      ...prev,
      goal: newValue
    }));
  };

  // Обработчик изменения типа диеты
  const handleDietTypeChange = (newValue) => {
    setFormData(prev => ({
      ...prev,
      dietType: newValue
    }));
  };
  
  // Предопределенные веса для быстрого выбора
  const predefinedWeights = [
    { value: '50', label: '50 кг' },
    { value: '60', label: '60 кг' },
    { value: '70', label: '70 кг' },
    { value: '80', label: '80 кг' }
  ];
  
  // Функция расчета
  const calculateMetabolism = () => {
    // Здесь должна быть вызвана функция основного расчета
    // В данном случае просто эмулируем действие, так как основная логика 
    // находится в родительском компоненте
    document.querySelector('button[type="submit"]')?.click();
  };

  // Текущий вес
  const currentWeight = parseFloatOrDefault(formData.weight, 70);
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-emerald-500" />
        Быстрая настройка параметров
      </h3>
      
      <div className="space-y-5">
        {/* Вес */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="weightSlider" className="text-gray-600 dark:text-gray-400">Вес</label>
            <span className="font-medium text-gray-900 dark:text-white">{formatNumber(currentWeight, 1)} кг</span>
          </div>
          <input
            type="range"
            id="weightSlider"
            min="40"
            max="150"
            step="1"
            value={currentWeight}
            onChange={handleWeightChange}
            aria-label="Вес"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-emerald-600"
          />
          
          {/* Кнопки с предопределенными весами */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedWeights.map(weight => (
              <button
                key={weight.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, weight: weight.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.weight === weight.value 
                    ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {weight.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Уровень активности */}
        <div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Уровень активности
          </div>
          <div className="grid grid-cols-1 gap-1">
            <button
              type="button"
              onClick={() => handleActivityChange('sedentary')}
              className={`p-2 text-xs rounded-lg border transition-colors text-left
                ${formData.activityLevel === 'sedentary' 
                  ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Сидячий</div>
              <div className="opacity-75">Минимальная активность</div>
            </button>
            <button
              type="button"
              onClick={() => handleActivityChange('light')}
              className={`p-2 text-xs rounded-lg border transition-colors text-left
                ${formData.activityLevel === 'light' 
                  ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Легкая активность</div>
              <div className="opacity-75">1-3 тренировки в неделю</div>
            </button>
            <button
              type="button"
              onClick={() => handleActivityChange('moderate')}
              className={`p-2 text-xs rounded-lg border transition-colors text-left
                ${formData.activityLevel === 'moderate' 
                  ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Умеренная активность</div>
              <div className="opacity-75">3-5 тренировок в неделю</div>
            </button>
            <button
              type="button"
              onClick={() => handleActivityChange('active')}
              className={`p-2 text-xs rounded-lg border transition-colors text-left
                ${formData.activityLevel === 'active' 
                  ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Высокая активность</div>
              <div className="opacity-75">6-7 тренировок в неделю</div>
            </button>
          </div>
        </div>

        {/* Цель */}
        <div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Ваша цель
          </div>
          <div className="grid grid-cols-3 gap-1">
            <button
              type="button"
              onClick={() => handleGoalChange('lose')}
              className={`p-2 text-xs rounded-lg border transition-colors
                ${formData.goal === 'lose' 
                  ? 'bg-amber-100 border-amber-300 text-amber-700 dark:bg-amber-900/30 dark:border-amber-700 dark:text-amber-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Снижение</div>
              <div className="opacity-75">веса</div>
            </button>
            <button
              type="button"
              onClick={() => handleGoalChange('maintain')}
              className={`p-2 text-xs rounded-lg border transition-colors
                ${formData.goal === 'maintain' 
                  ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Поддержание</div>
              <div className="opacity-75">веса</div>
            </button>
            <button
              type="button"
              onClick={() => handleGoalChange('gain')}
              className={`p-2 text-xs rounded-lg border transition-colors
                ${formData.goal === 'gain' 
                  ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Набор</div>
              <div className="opacity-75">массы</div>
            </button>
          </div>
        </div>

        {/* Тип диеты */}
        <div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Тип питания
          </div>
          <div className="grid grid-cols-2 gap-1">
            <button
              type="button"
              onClick={() => handleDietTypeChange('balanced')}
              className={`p-2 text-xs rounded-lg border transition-colors
                ${formData.dietType === 'balanced' 
                  ? 'bg-purple-100 border-purple-300 text-purple-700 dark:bg-purple-900/30 dark:border-purple-700 dark:text-purple-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Сбалансированное</div>
              <div className="opacity-75">30/30/40</div>
            </button>
            <button
              type="button"
              onClick={() => handleDietTypeChange('low_carb')}
              className={`p-2 text-xs rounded-lg border transition-colors
                ${formData.dietType === 'low_carb' 
                  ? 'bg-purple-100 border-purple-300 text-purple-700 dark:bg-purple-900/30 dark:border-purple-700 dark:text-purple-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Низкоуглеводное</div>
              <div className="opacity-75">35/45/20</div>
            </button>
            <button
              type="button"
              onClick={() => handleDietTypeChange('high_protein')}
              className={`p-2 text-xs rounded-lg border transition-colors
                ${formData.dietType === 'high_protein' 
                  ? 'bg-purple-100 border-purple-300 text-purple-700 dark:bg-purple-900/30 dark:border-purple-700 dark:text-purple-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Высокобелковое</div>
              <div className="opacity-75">40/30/30</div>
            </button>
            <button
              type="button"
              onClick={() => handleDietTypeChange('keto')}
              className={`p-2 text-xs rounded-lg border transition-colors
                ${formData.dietType === 'keto' 
                  ? 'bg-purple-100 border-purple-300 text-purple-700 dark:bg-purple-900/30 dark:border-purple-700 dark:text-purple-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Кетогенное</div>
              <div className="opacity-75">25/70/5</div>
            </button>
          </div>
        </div>
        
        {/* Кнопка пересчета */}
        <Button
          primary
          fullWidth
          onClick={calculateMetabolism}
          icon={<Activity className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать метаболизм
        </Button>
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;